import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  CreateOperatorResponse,
  CreateOperatorVars,
  UpdateOperatorResponse,
  UpdateOperatorVars
} from '@/common/api/params/operator'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { operatorEditPath } from '@/common/app/path'
import { useShowSavedToast } from '@/common/app/toast'
import { refreshCodeUnique } from '@/common/form/schema'
import { Operator } from '@/common/model/operator'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  DEFAULT_OPERATOR_SCHEMA,
  OperatorFormSchemaType,
  convertOperatorSchemaToVars,
  convertOperatorToSchema,
  operatorNameLabelMapping,
  operatorSchema
} from '@/components/container/form/modules/admin/operator/operatorSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  operator?: Operator
  onRefetch?(): void
}

const OperatorForm = ({ operator, onRefetch }: Props) => {
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<OperatorFormSchemaType>({
    resolver: zodResolver(operatorSchema),
    values: operator ? convertOperatorToSchema(operator) : DEFAULT_OPERATOR_SCHEMA,
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, operatorNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!operator })

  const { trigger } = useCallMutation<
    CreateOperatorResponse | UpdateOperatorResponse,
    CreateOperatorVars | UpdateOperatorVars
  >(
    operator ? ApiPath.UPDATE_OPERATOR : ApiPath.CREATE_OPERATOR,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        operator && onRefetch ? onRefetch() : navigate(operatorEditPath(response.id))
        reset(operator ? convertOperatorToSchema(operator) : DEFAULT_OPERATOR_SCHEMA)
        refreshCodeUnique()
      }
    },
    operator ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<OperatorFormSchemaType> = (data) =>
    trigger(convertOperatorSchemaToVars(data))

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={operator ? operator.name : 'オペレーターを新規作成する'}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {operator ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={operator ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={operator ? '未反映の変更があります' : '登録されていない新しいオペレーター'}
          onCancel={() =>
            reset(operator ? convertOperatorToSchema(operator) : DEFAULT_OPERATOR_SCHEMA)
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本情報</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='name'>
                名前
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('name')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.name?.message}
                  placeholder='山田 太郎'
                />
                <FormError message={errors.name?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required id='email'>
                メールアドレス
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('email')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.email?.message}
                  size={TextBoxSize.LARGE}
                  placeholder='admin@example.com'
                />
                <FormError message={errors.email?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required={!operator} id='password'>
                パスワード
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('password')}
                  fieldType={!operator ? FieldType.REQUIRED : undefined}
                  error={!!errors.password?.message}
                  type={FormTextFieldType.PASSWORD}
                  placeholder={operator ? '変更しない場合は空のままにしてください' : ''}
                />
                <FormError message={errors.password?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {operator ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default OperatorForm
